import { createColumnHelper } from '@tanstack/react-table';
import cn from 'classnames';
import { Fragment } from 'react';
import { useSuspenseMaxTagsQuery } from '#api/tags.queries.ts';
import { Link } from '#components/atoms/link/Link.tsx';
import { RouteLink } from '#components/patterns/entity-links/route-link/RouteLink.tsx';
import { NameCell } from '#components/patterns/table/name-cell/NameCell.tsx';
import { Table } from '#components/patterns/table/Table.tsx';
import type { Tag } from '#dn-types/tag.ts';
import { TagActionMenu } from './TagActionMenu.tsx';
import styles from './TagsList.module.css';

const columnHelper = createColumnHelper<Tag>();

const nameColumn = columnHelper.accessor('name', {
  header: 'Tag',
  size: 250,
  cell(info) {
    const tagName = info.getValue();
    const { description } = info.row.original;
    return (
      <NameCell
        name={tagName}
        nameTo={`/tags/${encodeURIComponent(tagName)}`}
        secondRow={<span className="truncate">{description}</span>}
      />
    );
  },
});

const hostsColumn = columnHelper.accessor('hostCount', {
  header: 'Hosts',
  size: 30,
  cell(info) {
    const count = info.getValue();
    if (!count) return count;
    const tagName = info.row.original.name;
    return (
      <Link className="a" to={`/hosts?filter.tags=${encodeURIComponent(tagName)}`}>
        {count}
      </Link>
    );
  },
  meta: { align: 'center', className: styles.TagsList_hosts },
});

const firewallRulesColumn = columnHelper.accessor('firewallRulesCount', {
  header: 'Rules',
  size: 50,
  cell(info) {
    return info.getValue() ?? 0;
  },
  meta: { align: 'center' },
});

const configsColumn = columnHelper.accessor('configOverrides', {
  header: 'Configs',
  size: 50,
  cell(info) {
    return info.getValue().length;
  },
  meta: {
    align: 'center',
    className: styles.TagsList_configs,
  },
});

const routesColumn = columnHelper.accessor('routeSubscriptions', {
  header: 'Routes',
  size: 50,
  meta: {
    truncate: true,
    className: styles.TagsList_routes,
  },
  cell(info) {
    const routes = info.getValue();

    if (!routes.length) {
      return null;
    }

    return (
      <div className="flex">
        {routes.map((id, index) => (
          <Fragment key={id}>
            <RouteLink className={'truncate'} routeId={id} key={id} />
            {index < routes.length - 1 ?
              <span>,&nbsp;</span>
            : null}
          </Fragment>
        ))}
      </div>
    );
  },
});

const actionMenuColumn = columnHelper.display({
  id: 'tag-action-menu',
  header: '',
  size: 34,
  cell(info) {
    const tagName = info.row.original.name;

    return (
      <div className="flex justify-end">
        <TagActionMenu tagName={tagName} />
      </div>
    );
  },
});

const columns = [nameColumn, hostsColumn, firewallRulesColumn, configsColumn, routesColumn, actionMenuColumn];

export function TagsList() {
  const {
    data: { data: tags },
    isFetching,
  } = useSuspenseMaxTagsQuery();
  return <Table data={tags} columns={columns} className={cn({ 'opacity-dim': isFetching })} />;
}
